import React, { useState } from 'react';
import { User, Lock, ArrowRight, Mail, Phone, MapPin, Calendar, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { ListenInsteadOfReading } from '../components/ListenInsteadOfReading';
export function LoginRegister() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1500);
  };
  return <div className="min-h-screen bg-gray-50 pt-40 lg:pt-52 pb-20" data-id="element-1241">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1242">
        {/* Page Header */}
        <div className="text-center mb-8" data-id="element-1243">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1244">
            Student Portal
          </h1>
          <p className="text-lg text-gray-600" data-id="element-1245">
            Access your on-demand classes or register to create an account
          </p>
        </div>

        {/* Important Notice */}
        <div className="mb-8 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl flex items-start gap-3 max-w-2xl mx-auto" data-id="element-1246">
          <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1247" />
          <div data-id="element-1248">
            <p className="text-sm font-semibold text-blue-900 mb-1" data-id="element-1249">
              For On-Demand Classes Only
            </p>
            <p className="text-sm text-blue-800" data-id="element-1250">
              This registration is for <strong data-id="element-1251">On-Demand Classes only</strong>.
              If you're taking Zoom or In-Person classes, you do not need to
              register here.
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-8" data-id="element-1252">
          <div className="inline-flex bg-white rounded-lg shadow-sm border border-gray-200 p-1" data-id="element-1253">
            <button onClick={() => setActiveTab('login')} className={`px-8 py-3 rounded-md font-semibold transition-all ${activeTab === 'login' ? 'bg-brand-purple-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1254">
              Log In
            </button>
            <button onClick={() => setActiveTab('register')} className={`px-8 py-3 rounded-md font-semibold transition-all ${activeTab === 'register' ? 'bg-brand-purple-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1255">
              Register
            </button>
          </div>
        </div>

        {/* Login Form */}
        {activeTab === 'login' && <Card className="max-w-md mx-auto p-8" data-id="element-1256">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-1257">
              Log In to Your Account
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1258">
              <div data-id="element-1259">
                <label htmlFor="login-username" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1260">
                  Username
                </label>
                <div className="relative" data-id="element-1261">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1262">
                    <User className="h-5 w-5 text-gray-400" data-id="element-1263" />
                  </div>
                  <input type="text" id="login-username" className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" placeholder="Enter your username" required data-id="element-1264" />
                </div>
              </div>

              <div data-id="element-1265">
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1266">
                  Password
                </label>
                <div className="relative" data-id="element-1267">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1268">
                    <Lock className="h-5 w-5 text-gray-400" data-id="element-1269" />
                  </div>
                  <input type="password" id="login-password" className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" placeholder="••••••••" required data-id="element-1270" />
                </div>
              </div>

              <Button type="submit" className="w-full font-semibold" size="lg" isLoading={isLoading} data-id="element-1271">
                Log In <ArrowRight className="ml-2 h-4 w-4" data-id="element-1272" />
              </Button>
            </form>

            <div className="mt-6 text-center" data-id="element-1273">
              <a href="/14.html" className="text-sm text-gray-600 hover:text-brand-purple-600 hover:underline" data-id="element-1274">
                Forgot your password?
              </a>
            </div>
          </Card>}

        {/* Registration Form */}
        {activeTab === 'register' && <Card className="max-w-2xl mx-auto p-8" data-id="element-1275">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center" data-id="element-1276">
              Create Your Account
            </h2>
            <p className="text-sm text-gray-600 mb-6 text-center" data-id="element-1277">
              Register for on-demand classes
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1278">
              {/* Personal Information */}
              <div data-id="element-1279">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1280">
                  <User className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1281" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1282">
                  <div data-id="element-1283">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1284">
                      Title
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1285">
                      <option data-id="element-1286">Mr.</option>
                      <option data-id="element-1287">Ms.</option>
                      <option data-id="element-1288">Mrs.</option>
                      <option data-id="element-1289">Dr.</option>
                    </select>
                  </div>
                  <div data-id="element-1290">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1291">
                      First Name<span className="text-red-500" data-id="element-1292">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1293" />
                  </div>
                  <div data-id="element-1294">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1295">
                      Last Name<span className="text-red-500" data-id="element-1296">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1297" />
                  </div>
                  <div data-id="element-1298">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1299">
                      Company
                    </label>
                    <input type="text" className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1300" />
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div data-id="element-1301">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1302">
                  <MapPin className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1303" />
                  Billing Address
                </h3>
                <div className="space-y-4" data-id="element-1304">
                  <div data-id="element-1305">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1306">
                      Address 1<span className="text-red-500" data-id="element-1307">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1308" />
                  </div>
                  <div data-id="element-1309">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1310">
                      Address 2
                    </label>
                    <input type="text" className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1311" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1312">
                    <div data-id="element-1313">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1314">
                        City<span className="text-red-500" data-id="element-1315">*</span>
                      </label>
                      <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1316" />
                    </div>
                    <div data-id="element-1317">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1318">
                        State/Province<span className="text-red-500" data-id="element-1319">*</span>
                      </label>
                      <select required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1320">
                        <option value="" data-id="element-1321">Select...</option>
                        <option value="AZ" data-id="element-1322">Arizona</option>
                        <option value="CA" data-id="element-1323">California</option>
                        <option value="NV" data-id="element-1324">Nevada</option>
                      </select>
                    </div>
                    <div data-id="element-1325">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1326">
                        Zip<span className="text-red-500" data-id="element-1327">*</span>
                      </label>
                      <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1328" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div data-id="element-1329">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1330">
                  <Phone className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1331" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1332">
                  <div data-id="element-1333">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1334">
                      Phone<span className="text-red-500" data-id="element-1335">*</span>
                    </label>
                    <input type="tel" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1336" />
                  </div>
                  <div data-id="element-1337">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1338">
                      Email<span className="text-red-500" data-id="element-1339">*</span>
                    </label>
                    <input type="email" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1340" />
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div data-id="element-1341">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1342">
                  Additional Information
                </h3>
                <div className="space-y-4" data-id="element-1343">
                  <div data-id="element-1344">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1345">
                      How did you hear about us?
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1346">
                      <option value="" data-id="element-1347">Select...</option>
                      <option data-id="element-1348">Google Search</option>
                      <option data-id="element-1349">Social Media</option>
                      <option data-id="element-1350">Referral</option>
                      <option data-id="element-1351">Other</option>
                    </select>
                  </div>
                  <div data-id="element-1352">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1353">
                      Are you on Facebook?
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1354">
                      <option value="" data-id="element-1355">Select...</option>
                      <option data-id="element-1356">Yes</option>
                      <option data-id="element-1357">No</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Username & Password */}
              <div data-id="element-1358">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1359">
                  <Lock className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1360" />
                  Create Your Username & Password
                </h3>
                <div className="space-y-4" data-id="element-1361">
                  <div data-id="element-1362">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1363">
                      Username<span className="text-red-500" data-id="element-1364">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1365" />
                  </div>
                  <div data-id="element-1366">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1367">
                      Password<span className="text-red-500" data-id="element-1368">*</span>
                    </label>
                    <input type="password" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1369" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1370">
                      Must be at least 8 characters
                    </p>
                  </div>
                  <div data-id="element-1371">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1372">
                      Confirm Password<span className="text-red-500" data-id="element-1373">*</span>
                    </label>
                    <input type="password" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1374" />
                  </div>
                </div>
              </div>

              {/* License Expiration Date */}
              <div data-id="element-1375">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1376">
                  <Calendar className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1377" />
                  License Expiration Date
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-1378">
                  <div data-id="element-1379">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1380">
                      Month
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1381">
                      <option data-id="element-1382">January</option>
                      <option data-id="element-1383">February</option>
                      <option data-id="element-1384">March</option>
                      <option data-id="element-1385">April</option>
                      <option data-id="element-1386">May</option>
                      <option data-id="element-1387">June</option>
                      <option data-id="element-1388">July</option>
                      <option data-id="element-1389">August</option>
                      <option data-id="element-1390">September</option>
                      <option data-id="element-1391">October</option>
                      <option data-id="element-1392">November</option>
                      <option data-id="element-1393">December</option>
                    </select>
                  </div>
                  <div data-id="element-1394">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1395">
                      Year
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1396">
                      <option data-id="element-1397">2024</option>
                      <option data-id="element-1398">2025</option>
                      <option data-id="element-1399">2026</option>
                      <option data-id="element-1400">2027</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-2" data-id="element-1401">
                  <a href="#" className="hover:underline" data-id="element-1402">
                    Click here to obtain your license expiration date from ADRE
                  </a>
                </p>
              </div>

              {/* Identity Confirmation */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1403">
                <div className="flex items-start" data-id="element-1404">
                  <input type="checkbox" id="identity-confirm" required className="mt-1 h-4 w-4 text-brand-purple-600 focus:ring-brand-purple-500 border-gray-300 rounded" data-id="element-1405" />
                  <label htmlFor="identity-confirm" className="ml-3 text-sm text-gray-700" data-id="element-1406">
                    <strong data-id="element-1407">I affirm this information to be true.</strong> The
                    Arizona Department of Real Estate requires that we confirm
                    your information and that you are the person taking the
                    classes. Incorporating a real person's name and license
                    number into a registration without the permission of both
                    parties, if you are NOT the person named in the
                    registration, we strongly recommend that you log out and not
                    continue. We reserve the right to use reasonable means to
                    verify your identity.
                  </label>
                </div>
              </div>

              {/* Privacy Statement */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1408">
                <h4 className="font-semibold text-blue-900 mb-2" data-id="element-1409">
                  Privacy Statement
                </h4>
                <p className="text-sm text-blue-800 mb-2" data-id="element-1410">
                  By registering with Desert Sage Seminars, you will create an
                  account by which we can administer your real estate license
                  renewal classes. Your information will be kept confidential
                  and not shared with any other party.
                </p>
                <p className="text-sm text-blue-800 mb-2" data-id="element-1411">
                  The Arizona Department of Real Estate requires that we confirm
                  your information and that you are the person taking the
                  classes.
                </p>
                <p className="text-sm text-blue-800" data-id="element-1412">
                  Payments for our classes are made through a 3rd party merchant
                  account secured by our site, and they do not share your credit
                  card information with us or any other party.
                </p>
              </div>

              {/* Submit Button */}
              <Button type="submit" className="w-full font-semibold" size="lg" isLoading={isLoading} data-id="element-1413">
                Create Account <CheckCircle2 className="ml-2 h-5 w-5" data-id="element-1414" />
              </Button>
            </form>
          </Card>}

        <ListenInsteadOfReading data-id="element-1415" />
      </div>
    </div>;
}